import { Component, PhysXComponent, Property } from '@wonderlandengine/api';
import { vec3 } from 'gl-matrix';

/**
 * bot
 */
export class Bot extends Component {
    static TypeName = 'bot';
    /* Properties that are configurable in the editor */
    static Properties = {
        speed: Property.float(10),
        arenaManager: Property.object(),
        player: Property.object(),
        bulletPool: Property.object(),
        botRoute: Property.object(),
    };

    start() {
        // Pre-allocate vectors to avoid GC every frame
        this._dir = vec3.create();
        this._toPlayer = vec3.create();
        this._forward = vec3.create();
        this._tempVec = vec3.create();
        this._up = vec3.fromValues(0, 1, 0);
        this._hidePos = vec3.fromValues(0, -1000, 0);
        this._deactivatePos = vec3.fromValues(0, -10, 0);
        
        this._visibilityTimer = 0; // Optional: for further throttling
        this._spreadOut = vec3.create();
        this.botPos = vec3.create();
        this.newPos = vec3.create();
        this.targetPos = vec3.create();
        
        this.waypoints = this.botRoute.children; // objects to getPositionWorld() from
        this.currentWaypointIndex = 0;
        this.threshold = 0.2; // Distance to consider 'arrived' at waypoint
        this.thresholdSq = this.threshold * this.threshold;
        
        this.network = this.arenaManager.getComponent('arena-handler');
        this.botId = 'bot-1';
        this.lastFireTime = 0;
        this.fireCooldown = 500; // ms
        this.health = 100;
        this.isDead = false;
        this.botIsActive = false;
        
        // Patrol/Combat state
        this.alertDurationMs = 3000; // How long bot stays in combat focus after being shot
        this.alertUntil = 0; // timestamp in ms
        this.fovCos = 0.6; // ~53 degrees field-of-view to detect player
        // Shooting spread (radians). 3-5 degrees feels fair for bots
        this.spreadRadians = (Math.PI / 180) * 4;
        this._playerVisibleCache = false;

        // Pre-allocate payload object for network messages to eliminate object allocations
        this._bulletPayload = {
            id: this.botId,
            pos: vec3.create(),
            linVel: vec3.create(),
            t: 0,
            weaponType: 'bot'
        };

        // Pre-bind methods to avoid closure allocations during execution
        this._boundRespawn = this._doRespawn.bind(this);

        if (this.engine && this.engine.onXRSessionEnd) {
            this.engine.onXRSessionEnd.add(this.onXRSessionEnd.bind(this));
        }
    }

    update(dt) {
        if (!this.botIsActive || this.isDead) return;
        if (!this.waypoints || this.waypoints.length === 0) return;

        const target = this.waypoints[this.currentWaypointIndex];
        target.getPositionWorld(this.targetPos);
        this.object.getPositionWorld(this.botPos);

        // 1. Calculate direction to waypoint using pre-allocated vector
        vec3.sub(this._dir, this.targetPos, this.botPos);
        const distSq = vec3.sqrLen(this._dir);

        if (distSq < this.thresholdSq) {
            this.currentWaypointIndex = (this.currentWaypointIndex + 1) % this.waypoints.length;
            return;
        }

        const dist = Math.sqrt(distSq);

        // 2. Movement logic (Lerp)
        const lerpFactor = Math.min(1, this.speed * dt / dist);
        vec3.lerp(this.newPos, this.botPos, this.targetPos, lerpFactor);
        this.object.setPositionWorld(this.newPos);
        
        // 3. Find the aim target
        let aimTarget = this.player;
        if (!this._aimTargetCache && this.player && this.player.children) {
            for (let i = 0; i < this.player.children.length; i++) {
                const child = this.player.children[i];
                if (child.name === "PlayerHead" || child.name === "PlayerBody") {
                    this._aimTargetCache = child;
                    break;
                }
            }
        }
        aimTarget = this._aimTargetCache || this.player;
        aimTarget.getPositionWorld(this.targetPos);

        // 4. Calculate vector to player
        vec3.sub(this._toPlayer, this.targetPos, this.botPos);
        vec3.normalize(this._toPlayer, this._toPlayer);

        // 5. Calculate forward vector (normalized direction to waypoint)
        vec3.normalize(this._forward, this._dir);

        // 6. FOV Check (Simple Dot Product)
        const dot = vec3.dot(this._forward, this._toPlayer);
        const nowMs = performance.now();
        const inAlert = nowMs < this.alertUntil;
        let playerVisible = this._playerVisibleCache;

        // 7. SINGLE RAYCAST: Only if in FOV or already Alerted
        if (dot >= this.fovCos || inAlert) {
            // Throttle expensive raycasts to reduce physics cost.
            if (this._visibilityTimer <= 0) {
                this._visibilityTimer = 0.12; // seconds (120ms)
                
                // Reset cache before checking
                this._playerVisibleCache = false;
                
                // Using a groupMask (e.g., 255) to limit what we hit
                const rayHit = this.engine.physics.rayCast(this.botPos, this._toPlayer, 255, 70);

                if (rayHit && rayHit.hitCount > 0) {
                    for (let i = 0; i < rayHit.hitCount; i++) {
                        const obj = rayHit.objects[i];
                        if (!obj || obj === this.object) continue;

                        const name = obj.name;
                        // If we hit the player, visibility is confirmed
                        if (name === "PlayerHead" || name === "PlayerBody" || name === "PhysicsPlayerBody") {
                            this._playerVisibleCache = true;
                            break;
                        }
                        // If we hit the map, the player is blocked
                        if (name === "Map" || name === "Mesh") break;
                    }
                }
                playerVisible = this._playerVisibleCache;
            }
        } else {
            // If not in FOV and not alerted, force false so they don't track behind them
            this._playerVisibleCache = false;
            playerVisible = false;
        }

        // Decrement visibility timer (seconds)
        if (this._visibilityTimer > 0) this._visibilityTimer -= dt;

        const inCombat = inAlert || playerVisible;

        // 8. Rotation Logic
        if (inCombat) {
            this.object.lookAt(this.targetPos, this._up);
        } else {
            // Look where moving
            vec3.add(this._tempVec, this.botPos, this._forward);
            this.object.lookAt(this._tempVec, this._up);
        }

        // 9. Shooting Logic (Using the result from the single raycast)
        if (playerVisible && nowMs - this.lastFireTime >= this.fireCooldown) {
            this.applySpreadInto(this._toPlayer, this.spreadRadians, this._spreadOut);
            vec3.scale(this._spreadOut, this._spreadOut, 1000);

            vec3.copy(this._bulletPayload.pos, this.botPos);
            vec3.copy(this._bulletPayload.linVel, this._spreadOut);
            this._bulletPayload.t = nowMs;

            this.network.handleBulletSpawn(this._bulletPayload);
            this.lastFireTime = nowMs;
        }
    }

    takeDamage(amount) {
        if (this.isDead) return;
        this.health -= amount;
        // Enter alert state when hit
        this.alertUntil = performance.now() + this.alertDurationMs;
        if (this.health <= 0) {
            this.isDead = true;
            this.onDeath();
        }
    }

    _doRespawn() {
        this._respawnTimeout = null;
        if (!this.botIsActive) return; // Guard against asynchronous state loops
        this.health = 100;
        this.isDead = false;
        this.currentWaypointIndex = 0; // Start at first waypoint
        if (this.waypoints && this.waypoints.length > 0) {
            this.waypoints[0].getPositionWorld(this.newPos);
            this.object.setPositionWorld(this.newPos);
        }
    }

    onDeath() {
        // Handle bot death (e.g., respawn, play animation, etc.)
        this.object.setPositionWorld(this._hidePos); // Hide bot for now
        // Optionally respawn after delay
        if (this._respawnTimeout) clearTimeout(this._respawnTimeout);
        this._respawnTimeout = setTimeout(this._boundRespawn, 10000); // 10 seconds
    }

    activate() {
        // Clear any pending respawn timers to prevent overlapping state updates
        if (this._respawnTimeout) {
            clearTimeout(this._respawnTimeout);
            this._respawnTimeout = null;
        }
        this.isDead = false;
        this.health = 100;
        this.botIsActive = true;
        // Respawn at the first waypoint
        if (this.waypoints && this.waypoints.length > 0) {
            this.currentWaypointIndex = 0;
            this.waypoints[0].getPositionWorld(this.newPos);
            this.object.setPositionWorld(this.newPos);
        }
    }
    
    deactivate() {
        this.botIsActive = false;
        // Move bot far below map to hide
        this.object.setPositionWorld(this._deactivatePos);
        // Cancel any pending respawn
        if (this._respawnTimeout) {
            clearTimeout(this._respawnTimeout);
            this._respawnTimeout = null;
        }
    }

    onXRSessionEnd() {
        // Reset bot state to initial values
        this.health = 100;
        this.isDead = false;
        this.botIsActive = false;
        this.currentWaypointIndex = 0;
        this.lastFireTime = 0;
        this.alertUntil = 0;
        // Move bot to initial position (first waypoint) or hide
        if (this.waypoints && this.waypoints.length > 0) {
            this.waypoints[0].getPositionWorld(this.newPos);
            this.object.setPositionWorld(this.newPos);
        } else {
            this.object.setPositionWorld(this._deactivatePos);
        }
    }

    // Zero-allocation spread writer: writes result into `out` (a vec3) and returns it
    applySpreadInto(direction, radians, out) {
        vec3.normalize(out, direction);
        const s = Math.tan(radians);
        out[0] += (Math.random() - 0.5) * 2 * s;
        out[1] += (Math.random() - 0.5) * 2 * s;
        out[2] += (Math.random() - 0.5) * 2 * s;
        vec3.normalize(out, out);
        return out;
    }
}